import * as React from 'react';
import {
  Alert,
  Button,
  Form,
  Modal,
  ProgressBar,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell, safeInternalPath } from './AuthShell';
import { PlanOptions } from './PlanOptions';
import {
  BillingInterval,
  clearRegistrationDraft,
  createSubscriptionCheckoutSession,
  getEmailRegistrationStatus,
  getSubscriptionCheckoutSessionStatus,
  makeEmptyRegistrationDraft,
  PlanOptionId,
  readRegistrationDraft,
  registerUser,
  RegistrationDraft,
  sendEnterpriseEnquiry,
  writeRegistrationDraft,
} from './api';
import { privacyPolicyDocument } from '../website-other/documents/privacyPolicyDocument';
import { termsAndConditionsDocument } from '../website-other/documents/termsAndConditionsDocument';
import { attendeeBookingCancellationRefundTerms } from '../website-other/documents/attendeeBookingCancellationRefundTerms';

type RegistrationStep = 'details' | 'plan-options' | 'institution-name' | 'completed';

const stepProgress: Record<RegistrationStep, number> = {
  details: 33,
  'plan-options': 67,
  'institution-name': 67,
  completed: 100,
};

const isPlanOptionId = (value: string | null): value is PlanOptionId =>
  value === 'personal' || value === 'pro' || value === 'premium' || value === 'enterprise';

const readDraftSafely = (): { draft: RegistrationDraft; error?: string } => {
  try {
    return { draft: readRegistrationDraft() };
  } catch {
    try {
      clearRegistrationDraft();
    } catch (storageError) {
      console.warn('Unable to clear invalid registration state.', storageError);
    }
    return {
      draft: makeEmptyRegistrationDraft(),
      error: 'Your saved registration could not be restored. Please start again.',
    };
  }
};

const useRegistration = () => {
  const initial = React.useMemo(readDraftSafely, []);
  const [draft, setDraftState] = React.useState(initial.draft);
  const [storageError, setStorageError] = React.useState(initial.error || '');

  const setDraft = React.useCallback((next: RegistrationDraft) => {
    try {
      writeRegistrationDraft(next);
      setDraftState(next);
      setStorageError('');
      return true;
    } catch {
      setStorageError('Registration details could not be saved in this browser.');
      return false;
    }
  }, []);

  return { draft, setDraft, storageError };
};

const RegistrationFrame = (props: {
  step: RegistrationStep;
  children: React.ReactNode;
}): React.JSX.Element => (
  <AuthShell
    wide={props.step === 'plan-options'}
    title="Create your Careerbook account"
    introduction="Complete the steps below to set up your account."
  >
    <ProgressBar
      now={stepProgress[props.step]}
      label={`${stepProgress[props.step]}%`}
      className="mb-4"
      aria-label="Registration progress"
    />
    {props.children}
  </AuthShell>
);

const DetailsStep = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { draft, setDraft, storageError } = useRegistration();
  const [form, setForm] = React.useState(() => ({
    ...draft,
    email: searchParams.get('email') || draft.email,
  }));
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (form.firstName.trim().length < 2 || form.lastName.trim().length < 2) {
      setError('Please enter your first and last name.');
      return;
    }
    if (form.password.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }

    setIsSubmitting(true);
    try {
      const status = await getEmailRegistrationStatus(form.email.trim());
      if (status.emailAddressAlreadyExists) {
        setError('An account already exists for that email address.');
        return;
      }

      const requestedPlan = searchParams.get('plan');
      const requestedBillingInterval: BillingInterval =
        searchParams.get('billingInterval') === 'yearly' ? 'yearly' : 'monthly';
      const planOptionId = isPlanOptionId(requestedPlan) ? requestedPlan : undefined;
      const next: RegistrationDraft = {
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        openInviteInstitutionId:
          typeof status.openInviteInstitutionId === 'number' ? status.openInviteInstitutionId : undefined,
        planOptionId,
        billingInterval: requestedBillingInterval,
        subscriptionStatus: 'not_started',
        subscriptionCheckoutSessionId: undefined,
        redirectToPath: safeInternalPath(searchParams.get('redirectToPath'), ''),
      };
      if (!setDraft(next)) return;

      if (typeof next.openInviteInstitutionId === 'number' || planOptionId === 'personal') {
        navigate('/auth/user-registration/completed');
      } else if (planOptionId === 'pro' || planOptionId === 'premium') {
        navigate('/auth/user-registration/institution-name');
      } else {
        navigate('/auth/user-registration/plan-options');
      }
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Unable to continue registration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RegistrationFrame step="details">
      {(error || storageError) && <Alert variant="danger">{error || storageError}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Stack gap={3}>
          <div className="d-flex gap-3 flex-column flex-md-row">
            <Form.Group controlId="registration-first-name" className="flex-fill">
              <Form.Label>First name</Form.Label>
              <Form.Control
                required
                autoComplete="given-name"
                value={form.firstName}
                onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="registration-last-name" className="flex-fill">
              <Form.Label>Last name</Form.Label>
              <Form.Control
                required
                autoComplete="family-name"
                value={form.lastName}
                onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              />
            </Form.Group>
          </div>
          <Form.Group controlId="registration-email">
            <Form.Label>Email address</Form.Label>
            <Form.Control
              type="email"
              required
              readOnly={searchParams.has('email')}
              autoComplete="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </Form.Group>
          <Form.Group controlId="registration-password">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
            <Form.Text>Use at least 12 characters.</Form.Text>
          </Form.Group>
          <Button className="align-self-start w-auto" type="submit" disabled={isSubmitting}>
            {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
            Continue
          </Button>
        </Stack>
      </Form>
      <p className="mt-4 mb-0">
        Already have an account? <Link to="/auth/login">Log in</Link>.
      </p>
    </RegistrationFrame>
  );
};

const PlanStep = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft, setDraft, storageError } = useRegistration();
  const [billingInterval, setBillingInterval] = React.useState(draft.billingInterval);
  const [enterpriseOpen, setEnterpriseOpen] = React.useState(false);
  const [enquiry, setEnquiry] = React.useState('');
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const hasDetails = Boolean(draft.firstName && draft.lastName && draft.email);

  React.useEffect(() => {
    if (!hasDetails) navigate('/auth/user-registration', { replace: true });
  }, [hasDetails, navigate]);

  const selectPlan = (planOptionId: PlanOptionId) => {
    if (planOptionId === 'enterprise') {
      setEnterpriseOpen(true);
      return;
    }
    const next = { ...draft, planOptionId, billingInterval };
    if (!setDraft(next)) return;
    navigate(
      planOptionId === 'personal'
        ? '/auth/user-registration/completed'
        : '/auth/user-registration/institution-name',
    );
  };

  const submitEnterprise = async () => {
    if (!enquiry.trim()) {
      setError('Please enter a short description of your requirements.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await sendEnterpriseEnquiry({ ...draft, billingInterval }, enquiry.trim());
      const next = { ...draft, password: '', planOptionId: 'enterprise' as const, billingInterval };
      if (!setDraft(next)) return;
      setEnterpriseOpen(false);
      navigate('/website/contact-us?subject=Enterprise%20plan');
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Unable to submit your enquiry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RegistrationFrame step="plan-options">
      {storageError && <Alert variant="danger">{storageError}</Alert>}
      <Button
        className="mb-3"
        type="button"
        variant="outline-secondary"
        onClick={() => navigate('/auth/user-registration')}
      >
        Back
      </Button>
      <PlanOptions
        billingInterval={billingInterval}
        onBillingIntervalChange={setBillingInterval}
        onSelect={selectPlan}
      />
      <Modal show={enterpriseOpen} onHide={() => setEnterpriseOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Enterprise enquiry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <p>Tell us about your organisation and what you need from Careerbook.</p>
          <Form.Control
            as="textarea"
            rows={6}
            value={enquiry}
            onChange={(event) => setEnquiry(event.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEnterpriseOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submitEnterprise} disabled={isSubmitting}>
            {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
            Submit enquiry
          </Button>
        </Modal.Footer>
      </Modal>
    </RegistrationFrame>
  );
};

const InstitutionStep = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { draft, setDraft, storageError } = useRegistration();
  const [institutionName, setInstitutionName] = React.useState(draft.institutionName);
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const subscriptionReturn = searchParams.get('subscription');
  const checkoutSessionId = searchParams.get('session_id');

  React.useEffect(() => {
    if (subscriptionReturn === 'cancel') {
      setError('Subscription setup was cancelled. You can try again below.');
      navigate('/auth/user-registration/institution-name', { replace: true });
      return;
    }
    if (subscriptionReturn !== 'success' || !checkoutSessionId) return;

    setIsSubmitting(true);
    getSubscriptionCheckoutSessionStatus(checkoutSessionId)
      .then((status) => {
        const complete = status.checkoutStatus === 'complete';
        const paid = status.paymentStatus === 'paid' || status.paymentStatus === 'no_payment_required';
        if (!complete || !paid) throw new Error(status.error || 'Subscription payment could not be verified.');
        const next = {
          ...draft,
          subscriptionStatus: 'active' as const,
          subscriptionCheckoutSessionId: checkoutSessionId,
        };
        if (!setDraft(next)) return;
        navigate('/auth/user-registration/completed', { replace: true });
      })
      .catch((verificationError) => {
        setError(verificationError instanceof Error ? verificationError.message : 'Subscription verification failed.');
      })
      .finally(() => setIsSubmitting(false));
  }, [checkoutSessionId, draft, navigate, setDraft, subscriptionReturn]);

  React.useEffect(() => {
    if (draft.planOptionId !== 'pro' && draft.planOptionId !== 'premium') {
      navigate('/auth/user-registration/plan-options', { replace: true });
    }
  }, [draft.planOptionId, navigate]);

  const beginCheckout = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!institutionName.trim()) {
      setError('Please enter your institution name.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const next = {
        ...draft,
        institutionName: institutionName.trim(),
        subscriptionStatus: 'pending' as const,
      };
      if (!setDraft(next)) return;
      const checkout = await createSubscriptionCheckoutSession(next);
      if (!checkout.url) throw new Error('The subscription checkout URL was not returned.');
      window.location.assign(checkout.url);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : 'Unable to start subscription checkout.');
      setIsSubmitting(false);
    }
  };

  return (
    <RegistrationFrame step="institution-name">
      {(error || storageError) && <Alert variant="danger">{error || storageError}</Alert>}
      {subscriptionReturn === 'success' ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-3 mb-0">Verifying your subscription...</p>
        </div>
      ) : (
        <Form onSubmit={beginCheckout}>
          <Stack gap={3}>
            <p className="mb-0">
              Enter your organisation name. You will then continue to Stripe to set up the subscription.
            </p>
            <Form.Group controlId="registration-institution-name">
              <Form.Label>Institution name</Form.Label>
              <Form.Control
                required
                value={institutionName}
                onChange={(event) => setInstitutionName(event.target.value)}
              />
            </Form.Group>
            <Stack direction="horizontal" gap={2}>
              <Button
                type="button"
                variant="outline-secondary"
                disabled={isSubmitting}
                onClick={() => navigate('/auth/user-registration/plan-options')}
              >
                Back
              </Button>
              <Button className="w-auto" type="submit" disabled={isSubmitting}>
                {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
                Continue to Stripe
              </Button>
            </Stack>
          </Stack>
        </Form>
      )}
    </RegistrationFrame>
  );
};

const CompletedStep = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft, storageError } = useRegistration();
  const [password, setPassword] = React.useState(draft.password);
  const [accepted, setAccepted] = React.useState({
    platformTerms: false,
    privacyPolicy: false,
    bookingTerms: false,
  });
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!draft.firstName || !draft.lastName || !draft.email) {
      navigate('/auth/user-registration', { replace: true });
    }
  }, [draft, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }
    if (!accepted.platformTerms || !accepted.privacyPolicy || !accepted.bookingTerms) {
      setError('You must accept all three documents to create an account.');
      return;
    }

    const planType =
      draft.planOptionId || (typeof draft.openInviteInstitutionId === 'number' ? 'personal' : undefined);
    if (!planType || planType === 'enterprise') {
      setError('Please choose an available registration plan.');
      return;
    }
    if (
      (planType === 'pro' || planType === 'premium') &&
      (draft.subscriptionStatus !== 'active' || !draft.subscriptionCheckoutSessionId)
    ) {
      setError('Please complete subscription setup before creating your account.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await registerUser({
        firstName: draft.firstName,
        lastName: draft.lastName,
        email: draft.email,
        password,
        planType,
        institutionName: draft.institutionName,
        subscriptionCheckoutSessionId: draft.subscriptionCheckoutSessionId,
        privacyPolicyAcceptedVersion: privacyPolicyDocument.version,
        platformTermsAcceptedVersion: termsAndConditionsDocument.version,
        bookingCancellationRefundTermsAcceptedVersion: attendeeBookingCancellationRefundTerms.version,
      });
      try {
        clearRegistrationDraft();
      } catch (storageError) {
        console.warn('Account created, but registration draft cleanup failed.', storageError);
      }
      const invitedPath =
        typeof draft.openInviteInstitutionId === 'number'
          ? `/website/institution/${draft.openInviteInstitutionId}`
          : '/';
      window.location.assign(safeInternalPath(draft.redirectToPath || null, invitedPath));
    } catch (registrationError) {
      setError(registrationError instanceof Error ? registrationError.message : 'Unable to create your account.');
      setIsSubmitting(false);
    }
  };

  return (
    <RegistrationFrame step="completed">
      {(error || storageError) && <Alert variant="danger">{error || storageError}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Stack gap={3}>
          {!draft.password && (
            <Form.Group controlId="registration-completion-password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Form.Text>
                For your security, your password is not saved. Please enter it again to complete registration.
              </Form.Text>
            </Form.Group>
          )}
          <Form.Check
            id="accept-platform-terms"
            checked={accepted.platformTerms}
            onChange={(event) => setAccepted({ ...accepted, platformTerms: event.target.checked })}
            label={
              <>
                I agree to the{' '}
                <a href="/website/platform-terms" target="_blank" rel="noreferrer">
                  Platform Terms of Service
                </a>
                .
              </>
            }
          />
          <Form.Check
            id="accept-privacy-policy"
            checked={accepted.privacyPolicy}
            onChange={(event) => setAccepted({ ...accepted, privacyPolicy: event.target.checked })}
            label={
              <>
                I have read the{' '}
                <a href="/website/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </>
            }
          />
          <Form.Check
            id="accept-booking-terms"
            checked={accepted.bookingTerms}
            onChange={(event) => setAccepted({ ...accepted, bookingTerms: event.target.checked })}
            label={
              <>
                I agree to the{' '}
                <a href="/website/attendee-booking-cancellation-refund-terms" target="_blank" rel="noreferrer">
                  Booking, Cancellation and Refund Terms
                </a>
                .
              </>
            }
          />
          <Stack direction="horizontal" gap={2}>
            <Button
              type="button"
              variant="outline-secondary"
              disabled={isSubmitting}
              onClick={() => {
                if (typeof draft.openInviteInstitutionId === 'number') {
                  navigate('/auth/user-registration');
                  return;
                }
                navigate(
                  draft.planOptionId === 'pro' || draft.planOptionId === 'premium'
                    ? '/auth/user-registration/institution-name'
                    : '/auth/user-registration/plan-options',
                );
              }}
            >
              Back
            </Button>
            <Button className="w-auto" type="submit" disabled={isSubmitting}>
              {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
              Create account
            </Button>
          </Stack>
        </Stack>
      </Form>
    </RegistrationFrame>
  );
};

export const WebsiteUserRegistrationPage = (props: {
  step: RegistrationStep;
}): React.JSX.Element => {
  switch (props.step) {
    case 'plan-options':
      return <PlanStep />;
    case 'institution-name':
      return <InstitutionStep />;
    case 'completed':
      return <CompletedStep />;
    default:
      return <DetailsStep />;
  }
};
